// organize-imports-ignore
import React from "react";
import { createRoot } from "react-dom/client";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom-v5-compat";

import * as Sentry from "@sentry/react";

import { initBrowserSupports } from "~/lib/browserSupport";
import history from "~/lib/history";
import { isDesktopApp } from "~/lib/utils/platform";
import { desktopVersion } from "~/lib/version";

import { Root } from "./RouterRoot";

// TODO: Once we're done moving over to the new UX, import these here
// import "~/css/legacy-semantic.css";
// import "~/css/text-variables.css";
// import "~/css/base.css";
import "~/css/App.css";
import "~/css/tailwind.css";
import "~/ui/variables.css";

window.HISTORY = history;

// Sentry configuration
if (window && window.SENTRY_DSN_PUBLIC) {
	Sentry.init({
		dsn: window.SENTRY_DSN_PUBLIC,
		environment: window.SENTRY_ENV,
		release: window.APP_VERSION,
		// If we load the electron sentry in this context before browser sentry loads
		// auto session tracking breaks the desktop app.
		autoSessionTracking: !isDesktopApp,
		maxBreadcrumbs: 100,
		beforeBreadcrumb(crumb) {
			if (
				crumb.category === "xhr" &&
				["https://api.segment.io", "https://stats.g.doubleclick.net", "https://in.hotjar.com"].some(e =>
					crumb.data?.url?.startsWith(e),
				)
			) {
				return null;
			}
			return crumb;
		},
		beforeSend(event, hint) {
			// This error is fired by the Outlook Safe Links scraper by some of their
			// internal operations. The scraper also fires a ton more errors that we
			// can't do anything about, so if we detect it, we deactivate Sentry for
			// this session.
			// For more info about these errors see: https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-865857552
			// and https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/20
			if (
				typeof hint?.originalException === "string" &&
				hint.mechanism?.type === "onunhandledrejection" &&
				hint.originalException.startsWith("Object Not Found Matching Id:")
			) {
				Sentry.close(0);
				return null;
			}
			return event;
		},
		// Filter out reCAPTCHA timeout errors — both old-style non-Error promise
		// rejections ("Timeout") and new-style Error objects ("reCAPTCHA Timeout")
		// See: https://github.com/getsentry/sentry-javascript/issues/2514
		ignoreErrors: ["Non-Error promise rejection captured with value: Timeout", "reCAPTCHA Timeout"],
		integrations: [
			Sentry.thirdPartyErrorFilterIntegration({
				filterKeys: ["fellow-app-unique-key-9DLSlDeFzMKrig"],
				// This will filter out stack traces that do not contain any of our functions
				behaviour: "drop-error-if-exclusively-contains-third-party-frames",
			}),
			Sentry.reactRouterV6BrowserTracingIntegration({
				useEffect: React.useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
		],
		tracesSampleRate: process.env.NODE_ENV === "production" ? 0.05 : 1.0,
	});

	const currentScope = Sentry.getCurrentScope();
	currentScope.setTag("client_session_id", window.CLIENT_SESSION_ID);
	if (window.INITIAL_STATE?.account?.id) {
		currentScope.setTag("account_id", String(window.INITIAL_STATE.account.id));
	}
	if (window.INITIAL_STATE?.user?.id) {
		currentScope.setUser({ id: String(window.INITIAL_STATE.user.id) });
	}
	try {
		const resolvedDesktopVersion = desktopVersion();
		if (resolvedDesktopVersion) {
			currentScope.setTag("desktop_app_version", resolvedDesktopVersion.join("."));
		}
	} catch (error) {
		Sentry.captureException(error);
	}
}

function main(): void {
	const root = document.getElementById("root");
	if (root) {
		createRoot(root).render(React.createElement(Root));
	}
}

main();

if (window) {
	window.addEventListener("keydown", (e: KeyboardEvent) => {
		if ([8, 32].includes(e.keyCode) && window.document && e.target == window.document.body) {
			e.preventDefault();
		}
	});

	initBrowserSupports();
	document.documentElement.classList.toggle("Nav--ReducedMainWidth");
}
